import React, { useState } from "react";
import {
  Box,
  Flex,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useColorModeValue,
  Button,
  Badge,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  HStack,
  VStack,
  Avatar,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  SimpleGrid,
  Icon,
} from "@chakra-ui/react";
import Card from "../components/Card/Card";
import CardBody from "../components/Card/CardBody";
import CardHeader from "../components/Card/CardHeader";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from 'react-query';
import { FaUser, FaCalendarAlt, FaClock, FaEye } from "react-icons/fa";
import { getClientById } from '../services/clientService';
import { getAppointmentsByClient } from '../services/appointmentService';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import JalaliDateDisplay from '../components/JalaliDateDisplay';
import api from '../services/api';

function ClientProfile() {
  const { id: clientId } = useParams();
  const navigate = useNavigate();
  const { user, isConsultant } = useAuth();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  
  const textColor = useColorModeValue("gray.700", "white");
  const bgCard = useColorModeValue("white", "gray.700");
  const borderColor = useColorModeValue("gray.200", "gray.600");

  // دریافت اطلاعات مراجع
  const {
    data: clientData,
    isLoading: isLoadingClient,
    error: clientError
  } = useQuery(
    ['client', clientId],
    () => getClientById(clientId),
    {
      enabled: !!clientId,
      staleTime: 5 * 60 * 1000, // 5 دقیقه
    }
  );



  // دریافت ملاقات‌های مراجع
  const {
    data: appointmentsData,
    isLoading: isLoadingAppointments,
    error: appointmentsError
  } = useQuery(
    ['client-appointments', clientId],
    () => getAppointmentsByClient(clientId),
    {
      enabled: !!clientId,
      staleTime: 2 * 60 * 1000, // 2 دقیقه
    }
  );

  // بررسی دسترسی مشاور
  const canAccessClient = () => {
    if (!clientData) return false;
    
    // اگر کاربر مدیر است، دسترسی دارد
    if (!isConsultant()) return true;
    
    const client = clientData?.data?.client || clientData?.client || clientData;
    
    // اگر مشاور است، باید در فیلد consultant مراجع ثبت شده باشد
    const isClientConsultant = client.consultant === user.id || 
                              client.consultant?._id === user.id ||
                              client.consultantId === user.id;
    
    return isClientConsultant;
  };

  const handleViewAppointmentDetails = (appointment) => {
    setSelectedAppointment(appointment);
    onOpen();
  };

  const handleDownloadFile = async (file) => {
    try {
      const response = await api.get(`/files/${file._id || file.id}`, {
        responseType: 'blob'
      });
      
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.customName || file.originalName || file.fileName || 'file';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('فایل با موفقیت دانلود شد');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('خطا در دانلود فایل');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'scheduled':
        return 'green';
      case 'pending':
        return 'yellow';
      case 'cancelled':
        return 'red';
      case 'completed':
        return 'blue';
      case 'no-show':
        return 'orange';
      default:
        return 'gray';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'scheduled':
        return 'تأیید شده';
      case 'pending':
        return 'در انتظار';
      case 'cancelled':
        return 'لغو شده';
      case 'completed':
        return 'انجام شده';
      case 'no-show':
        return 'حضور نیافت';
      default:
        return 'نامشخص';
    }
  };

  const getPaymentColor = (paymentStatus) => {
    switch (paymentStatus) {
      case 'paid':
        return 'green';
      case 'pending':
        return 'yellow';
      case 'partial':
        return 'orange';
      default:
        return 'gray';
    }
  };

  const getPaymentText = (paymentStatus) => {
    switch (paymentStatus) {
      case 'paid':
        return 'پرداخت شده';
      case 'pending':
        return 'در انتظار';
      case 'partial':
        return 'نیمه پرداخت';
      default:
        return 'نامشخص';
    }
  };

  // اگر در حال بارگذاری است
  if (isLoadingClient || isLoadingAppointments) {
    return (
      <Flex direction="column" pt={{ base: "120px", md: "75px" }} dir="rtl">
        <Box mx="auto" p={{ base: "20px", md: "30px" }} pe="20px" minH="100vh">
          <Box bg={bgCard} borderRadius="15px" boxShadow="0 20px 27px 0 rgb(0 0 0 / 5%)" p="20px">
            <Flex justify="center" align="center" minH="400px">
              <Spinner size="xl" color="teal.500" />
              <Text mr={4} fontSize="lg" color={textColor}>
                در حال بارگذاری اطلاعات مراجع...
              </Text>
            </Flex>
          </Box>
        </Box>
      </Flex>
    );
  }

  // اگر خطا وجود دارد
  if (clientError || appointmentsError) {
    return (
      <Flex direction="column" pt={{ base: "120px", md: "75px" }} dir="rtl">
        <Box mx="auto" p={{ base: "20px", md: "30px" }} pe="20px" minH="100vh">
          <Box bg={bgCard} borderRadius="15px" boxShadow="0 20px 27px 0 rgb(0 0 0 / 5%)" p="20px">
            <Alert status="error">
              <AlertIcon />
              <AlertTitle>خطا!</AlertTitle>
              <AlertDescription>
                خطا در بارگذاری اطلاعات مراجع. لطفاً دوباره تلاش کنید.
              </AlertDescription>
            </Alert>
          </Box>
        </Box>
      </Flex>
    );
  }

  // بررسی دسترسی
  if (!canAccessClient()) {
    return (
      <Flex direction="column" pt={{ base: "120px", md: "75px" }} dir="rtl">
        <Box mx="auto" p={{ base: "20px", md: "30px" }} pe="20px" minH="100vh">
          <Box bg={bgCard} borderRadius="15px" boxShadow="0 20px 27px 0 rgb(0 0 0 / 5%)" p="20px">
            <Alert status="warning">
              <AlertIcon />
              <AlertTitle>دسترسی محدود!</AlertTitle>
              <AlertDescription>
                شما دسترسی به این پروفایل مراجع ندارید.
              </AlertDescription>
            </Alert>
          </Box>
        </Box>
      </Flex>
    );
  }

  const client = clientData?.data?.client || clientData?.client || clientData;
  const appointments = appointmentsData?.data?.appointments || appointmentsData?.appointments || [];

  return (
    <>
      <Flex direction="column" pt={{ base: "120px", md: "75px" }} dir="rtl">
        <Box mx="auto" p={{ base: "20px", md: "30px" }} pe="20px" minH="100vh">
          <Box bg={bgCard} borderRadius="15px" boxShadow="0 20px 27px 0 rgb(0 0 0 / 5%)" p="20px">
            
            {/* Header */}
            <Flex justify="space-between" align="center" mb={6}>
              <Text fontSize="2xl" fontWeight="bold" color={textColor}>
                پروفایل مراجع
              </Text>
              <Button
                colorScheme="gray"
                variant="outline"
                onClick={() => navigate(-1)}
              >
                بازگشت
              </Button>
            </Flex>

            {/* اطلاعات اصلی مراجع */}
            <Card mb={6}>
              <CardHeader>
                <Flex align="center">
                  <Avatar
                    size="lg"
                    name={client.firstName && client.lastName 
                      ? `${client.firstName} ${client.lastName}`
                      : client.name || 'نامشخص'
                    }
                    bg="teal.500"
                    me={4}
                  />
                  <VStack align="start" spacing={1}>
                    <Text fontSize="xl" fontWeight="bold" color={textColor}>
                      {client.firstName && client.lastName 
                        ? `${client.firstName} ${client.lastName}`
                        : client.name || 'نامشخص'
                      }
                    </Text>
                    <Text fontSize="sm" color="gray.400">
                      کد مراجع: {client._id || client.id}
                    </Text>
                  </VStack>
                </Flex>
              </CardHeader>
              <CardBody>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                  <VStack align="start" spacing={3}>
                    <HStack>
                      <Icon as={FaUser} color="gray.400" />
                      <Text color={textColor}>
                        <strong>کد ملی:</strong> {client.nationalId || 'ثبت نشده'}
                      </Text>
                    </HStack>
                  </VStack>
                  <VStack align="start" spacing={3}>
                    <Text color={textColor}>
                      <strong>شغل:</strong> {client.occupation || 'ثبت نشده'}
                    </Text>
                    <Text color={textColor}>
                      <strong>تاریخ تولد:</strong> {client.birthDate ? (
                        <JalaliDateDisplay
                          date={client.birthDate}
                          format="jYYYY/jMM/jDD"
                        />
                      ) : 'ثبت نشده'}
                    </Text>
                    <Text color={textColor}>
                      <strong>جنسیت:</strong> {client.gender === 'male' ? 'مرد' : client.gender === 'female' ? 'زن' : 'ثبت نشده'}
                    </Text>
                  </VStack>
                </SimpleGrid>
              </CardBody>
            </Card>

            {/* لیست ملاقات‌ها */}
            <Card>
              <CardHeader>
                <Text fontSize="lg" fontWeight="bold" color={textColor}>
                  ملاقات‌ها ({appointments.length})
                </Text>
              </CardHeader>
              <CardBody>
                <Box overflowX={{ sm: "scroll", xl: "hidden" }}>
                  <Table variant="simple" color={textColor} size="sm">
                    <Thead>
                      <Tr my=".8rem" pl="0px" color="gray.400">
                        <Th borderColor={borderColor} textAlign="right">
                          تاریخ و ساعت
                        </Th>
                        <Th borderColor={borderColor} textAlign="right">
                          یادداشت مشاور
                        </Th>
                        <Th borderColor={borderColor} textAlign="right">
                          عملیات
                        </Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {appointments.length === 0 ? (
                        <Tr>
                          <Td colSpan={3} textAlign="center" py={8}>
                            <Text color="gray.500">هیچ ملاقات‌ای یافت نشد</Text>
                          </Td>
                        </Tr>
                      ) : (
                        appointments.map((appointment) => (
                          <Tr key={appointment._id || appointment.id}>
                            <Td>
                              <Flex direction="column" alignItems="flex-start">
                                <HStack spacing={2} mb={1}>
                                  <FaCalendarAlt color="gray.400" />
                                  <JalaliDateDisplay
                                    date={appointment.date}
                                    format="jYYYY/jMM/jDD"
                                    textProps={{
                                      fontSize: "sm",
                                      color: textColor
                                    }}
                                  />
                                </HStack>
                                <HStack spacing={2}>
                                  <FaClock color="gray.400" />
                                  <Text fontSize="sm" color={textColor}>
                                    {appointment.time || 'ساعت ثبت نشده'}
                                  </Text>
                                </HStack>
                              </Flex>
                            </Td>
                            <Td>
                              <Text fontSize="sm" color={textColor} maxW="300px" noOfLines={2}>
                                {appointment.consultantNotes || 'یادداشتی ثبت نشده'}
                              </Text>
                            </Td>
                            <Td>
                              <Button
                                size="sm"
                                colorScheme="teal"
                                variant="outline"
                                leftIcon={<FaEye />}
                                onClick={() => navigate(`/appointment/${appointment._id || appointment.id}`)}
                              >
                                مشاهده جزئیات
                              </Button>
                            </Td>
                          </Tr>
                        ))
                      )}
                    </Tbody>
                  </Table>
                </Box>
              </CardBody>
            </Card>
          </Box>
        </Box>
      </Flex>

      {/* Modal جزئیات ملاقات */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>جزئیات ملاقات</ModalHeader>
          <ModalCloseButton right="auto" left={4} />
          <ModalBody pb={6}>
            {selectedAppointment && (
              <VStack spacing={4} align="start">
                <Box>
                  <Text fontWeight="bold" mb={2}>مشاور:</Text>
                  <Text>{selectedAppointment.consultant?.name || selectedAppointment.consultantName || 'نامشخص'}</Text>
                </Box>
                <Box>
                  <Text fontWeight="bold" mb={2}>تاریخ:</Text>
                  <JalaliDateDisplay
                    date={selectedAppointment.date}
                    format="jYYYY/jMM/jDD"
                  />
                </Box>
                <Box>
                  <Text fontWeight="bold" mb={2}>ساعت:</Text>
                  <Text>{selectedAppointment.time || 'ثبت نشده'}</Text>
                </Box>
                <Box>
                  <Text fontWeight="bold" mb={2}>مدت:</Text>
                  <Text>{selectedAppointment.duration || '0'} دقیقه</Text>
                </Box>
                <Box>
                  <Text fontWeight="bold" mb={2}>نوع ملاقات:</Text>
                  <Text>
                    {selectedAppointment.meetingType === 'in-person' ? 'حضوری' : 
                     selectedAppointment.meetingType === 'phone' ? 'تلفنی' : 
                     selectedAppointment.meetingType === 'online' ? 'آنلاین' : 'نامشخص'}
                  </Text>
                </Box>
                <Box>
                  <Text fontWeight="bold" mb={2}>هزینه:</Text>
                  <Text>{selectedAppointment.cost || '0'} تومان</Text>
                </Box>
                <Box>
                  <Text fontWeight="bold" mb={2}>وضعیت:</Text>
                  <Badge
                    bg={`${getStatusColor(selectedAppointment.status)}.400`}
                    color="white"
                    fontSize="16px"
                    p="3px 10px"
                    borderRadius="8px"
                  >
                    {getStatusText(selectedAppointment.status)}
                  </Badge>
                </Box>
                <Box>
                  <Text fontWeight="bold" mb={2}>وضعیت پرداخت:</Text>
                  <Badge
                    bg={`${getPaymentColor(selectedAppointment.paymentStatus)}.400`}
                    color="white"
                    fontSize="16px"
                    p="3px 10px"
                    borderRadius="8px"
                  >
                    {getPaymentText(selectedAppointment.paymentStatus)}
                  </Badge>
                </Box>
                {selectedAppointment.consultantNotes && (
                  <Box>
                    <Text fontWeight="bold" mb={2}>یادداشت‌های مشاور:</Text>
                    <Text p={3} bg="gray.50" borderRadius="md" border="1px" borderColor="gray.200">
                      {selectedAppointment.consultantNotes}
                    </Text>
                  </Box>
                )}
                {selectedAppointment.notes && (
                  <Box>
                    <Text fontWeight="bold" mb={2}>یادداشت‌های عمومی:</Text>
                    <Text p={3} bg="blue.50" borderRadius="md" border="1px" borderColor="blue.200">
                      {selectedAppointment.notes}
                    </Text>
                  </Box>
                )}
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
}

export default ClientProfile; 